import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabaseClient';
import { PRODUCT_CACHE_TIME } from './queryKeys';

export interface ProductType {
  id: number;
  name: string;
}

export interface UseProductTypesResult {
  productTypes: ProductType[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  refetch: () => void;
}

export function useProductTypes(locale: string = 'en'): UseProductTypesResult {
  const cacheKey = ['productTypes', 'all', locale] as const;

  const query = useQuery({
    queryKey: cacheKey,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_client_product_types', {
        payload: { locale },
      });

      if (error) {
        console.error('[useProductTypes] Error:', error);
        throw new Error(`Failed to fetch product types: ${error.message}`);
      }

      return ((data as any)?.data as ProductType[]) ?? [];
    },
    staleTime: PRODUCT_CACHE_TIME.filters,
    gcTime: PRODUCT_CACHE_TIME.filters * 2,
    retry: 2,
  });

  return {
    productTypes: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error as Error | null,
    refetch: query.refetch as () => void,
  };
}
